/* eslint-disable */

/**
 * An enum representation of the days of the week, consistent with the ISO 8601 standard and
 * {@link org.joda.time.DateTimeConstants}
 */
export enum DayOfWeek {
  UNKNOWN_DO_NOT_USE = "UNKNOWN_DO_NOT_USE",
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

/** Represents a month in the Gregorian calendar. */
export enum MonthOfYear {
  MONTH_OF_YEAR_UNSPECIFIED = "MONTH_OF_YEAR_UNSPECIFIED",
  JANUARY = "JANUARY",
  FEBRUARY = "FEBRUARY",
  MARCH = "MARCH",
  APRIL = "APRIL",
  MAY = "MAY",
  JUNE = "JUNE",
  JULY = "JULY",
  AUGUST = "AUGUST",
  SEPTEMBER = "SEPTEMBER",
  OCTOBER = "OCTOBER",
  NOVEMBER = "NOVEMBER",
  DECEMBER = "DECEMBER",
}

/**
 * Canonical representation for date/times where both UTC and
 * localtime must be representable and where localtime algebra, e.g.,
 * "same time yesterday", must computable.
 *
 * Draws from Joda-Time and the contents of the (never finalized)
 * Time-Zone/Timezone header draft spec
 * (http://tools.ietf.org/html/draft-sharhalakis-httptz-05)
 *
 * The RFC draft specs the first field, called instance_us below, to
 * be formatted as rfc3339, a string that does not include fractional
 * seconds. We generalize to an epoch-relative 64bit integer in
 * microseconds and store the timezone offset separately.
 *
 * We choose microseconds (usec) over milliseconds (msec) because on
 * mobile devices multiple things of interest can occur within the
 * same millisecond. The cost of usec over msec when the last three
 * digits are 0 is ~1 byte. The instance_us type is signed, to allow
 * for pre-epoch dates but not sint64 since virtually all dates should
 * be post-epoch.
 *
 * tz_names are defined in the tz database:
 *
 *   http://en.wikipedia.org/wiki/Tz_database
 *
 * Using an enum was considered, prototyped, and rejected: the version
 * of the database varies by platform and distribution and can't be
 * relied upon to be in sync with an enum.
 *
 * Storing the timezone offset is insufficient in the face of daylight
 * savings time, e.g., you can't represent "same hour of the day
 * yesterday". As long as tz_name is known, tz_offset is not needed
 * but tz_name is not always available. Joda-Time stores
 * timezone_offset as msec, which is a understandable since Joda
 * pretty much stores everything in msec. Storing it in usec (to be
 * the same as instance_usec) seems of little value since the current
 * most significant use is quarter hours.
 */
export interface DateTime {
  /**
   * in usec; datum reference: the Unix epoch
   * What Joda calls instant except in usec rather than msec.
   *
   * Range: 1970 +/- 300K years
   */
  instant_usec?: number;
  /**
   * in minutes; datum reference: UTC
   * What Joda calls offset except in minutes rather than msec.
   */
  timezone_offset_min?: number;
  /** as defined in draft-sharhalakis-httptz-05 */
  posix_tz?: string;
  /** as defined in draft-sharhalakis-httptz-05 */
  tz_name?: string[];
  /**
   * An arbitrary value that can be used to break sorting ties when
   * instant_usec does not provide sufficient granularity (e.g.
   * System.nanoTime() in Java)
   */
  ordinal?: number;
}

export interface DateTimeInterval {
  inclusive_start?: DateTime;
  exclusive_end?: DateTime;
}

/**
 * A representation of a particular month in a year without any
 * associated timezone information.
 *
 * Based on org.joda.time.YearMonth. Conceptually the fixed
 * precision makes this a useful construct for reporting and
 * mapping into external systems, but it cannot be effectively
 * used for computation. In other words, this is really an ID
 * that happens to look like a date.
 *
 * We've elided the concept of chronology because we do not have
 * a need to represent pre-Gregorian dates.
 */
export interface YearMonth {
  /** the full year. Eg, `2013` */
  year?: number;
  /** the one-indexed month of the year. Eg, `7` for July */
  month_of_year?: number;
}

/**
 * A representation of a particular day in a year without any
 * associated timezone information.
 *
 * Similar to YearMonth and LocalDate in JodaTime. Note that,
 * YearMonthDay is deprecated in JodaTime in favor of LocalDate's
 * more thorough API. We chose to use YearMonthDay for naming
 * consistency and because the name is very representative of
 * the data format.
 */
export interface YearMonthDay {
  /** the full year. Eg, `2013` */
  year?: number;
  /** the one-indexed month of the year. Eg, `7` for July */
  month_of_year?: number;
  /** the one-indexed day of the month. Eg, `4` for the U.S. independence day */
  day_of_month?: number;
}

/**
 * A proto representation of {@link org.joda.time.LocalTime}, which represents a
 * time without a time zone.
 */
export interface LocalTime {
  hour_of_day?: number;
  minute_of_hour?: number;
  second_of_minute?: number;
  millis_of_second?: number;
}

/** A day of the week and a local time. */
export interface DayTime {
  /** The day of week constants defined in {@link org.joda.time.DateTimeConstants} */
  day_of_week?: number;
  time_at?: LocalTime;
}
